import { Config, UserRole } from "@employee-management/common";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useMemo, useEffect, useCallback } from "react";
import { z } from "zod";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Textarea } from "@/components/ui/textarea";
import { apiService } from "@/lib/api-service";
import { DatePicker } from "@/components/ui/date-picker";
import { parseValidationErrors } from "@/lib/parse-validation-errors.util";
import { toast } from "sonner";
import type { OvertimeEntry } from "@/types/project.types";
import { Separator } from "@/components/ui/separator";
import { onKeyDownNumberOnly } from "@/lib/number-input.util";

const overtimeEmployeeSchemaWithFinancial = z.object({
  employeeId: z.string().cuid("Employee ID must be selected"),
  hours: z.number().positive("Hours must be positive"),
  rate: z.number().positive("Rate must be positive").optional(),
  total: z.number().positive("Total must be positive").optional(),
  notes: z.string().optional(),
});

const overtimeEditFormSchemaWithFinancial = z.object({
  notes: z.string().optional(),
  date: z.string().min(1, "Date is required"),
  employees: z
    .array(overtimeEmployeeSchemaWithFinancial)
    .min(1, "At least one employee is required"),
});

type OvertimeEditFormData = z.infer<typeof overtimeEditFormSchemaWithFinancial>;

interface OvertimeEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  overtimeEntry: OvertimeEntry;
  projectMembers: Array<{
    id: string;
    name: string;
    email: string;
    department: string;
    hourlyRate: number;
  }>;
  onComplete: () => void;
}

export function OvertimeEditDialog({
  open,
  onOpenChange,
  overtimeEntry,
  projectMembers,
  onComplete,
}: OvertimeEditDialogProps) {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();

  // Only managers can edit overtime entries
  if (!hasRole(UserRole.MANAGER)) {
    return null;
  }

  const {
    register,
    setValue,
    watch,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    getFieldState,
  } = useForm<OvertimeEditFormData>({
    resolver: zodResolver(overtimeEditFormSchemaWithFinancial),
    defaultValues: {
      notes: overtimeEntry.notes || "",
      date: new Date(overtimeEntry.date).toISOString().split("T")[0],
      employees: overtimeEntry.employees.map((emp) => ({
        employeeId: emp.employeeId,
        hours: emp.hours,
        rate: emp.rate || 0,
        total: emp.total || 0,
        notes: emp.notes || "",
      })),
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "employees",
  });

  const employees = useWatch({
    name: "employees",
    control,
  });

  const selectedEmployeeIds = useMemo(
    () => employees.map((emp) => emp.employeeId).filter(Boolean),
    [employees]
  );

  const canAddMore = employees.length < projectMembers.length;

  const availableEmployeeOptions: ComboboxOption[] = projectMembers.map(
    (member) => ({
      value: member.id,
      label: member.name,
      description: `${member.department} • ${member.email}`,
      disabled: selectedEmployeeIds.includes(member.id),
    })
  );

  // Calculate total for an employee
  const calculateTotal = useCallback(
    (index: number) => {
      const employee = watch(`employees.${index}`);
      if (Number(employee?.hours) >= 0 && Number(employee?.rate) >= 0) {
        const total = Number(employee.hours) * Number(employee.rate);
        setValue(`employees.${index}.total`, total);
        return total;
      }
      return 0;
    },
    [setValue, watch]
  );

  // Auto-fill rate when employee is selected
  const handleEmployeeSelect = (index: number, id: string) => {
    const member = projectMembers.find((m) => m.id === id);
    if (member) {
      setValue(`employees.${index}.rate`, member.hourlyRate);
      calculateTotal(index);
    }
  };

  useEffect(() => {
    if (open) {
      reset({
        notes: overtimeEntry.notes || "",
        date: new Date(overtimeEntry.date).toISOString().split("T")[0],
        employees: overtimeEntry.employees.map((emp) => ({
          employeeId: emp.employeeId,
          hours: emp.hours,
          rate: emp.rate || 0,
          total: emp.total || 0,
          notes: emp.notes || "",
        })),
      });
    }
  }, [open, overtimeEntry, reset]);

  const updateOvertimeMutation = useMutation({
    mutationFn: async (data: OvertimeEditFormData) => {
      const overtimeData = {
        ...data,
        date: new Date(data.date),
      };

      await apiService.put(`/overtime/${overtimeEntry.id}`, overtimeData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["overtime", "project", overtimeEntry.project.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["project", overtimeEntry.project.id],
      });
      queryClient.invalidateQueries({ queryKey: ["overtime", "pending"] });
      queryClient.invalidateQueries({
        queryKey: ["project-revision-history", overtimeEntry.project.id],
      });
      reset();
      onComplete();
    },
    onError: (error: unknown) => {
      console.log(error);

      parseValidationErrors<OvertimeEditFormData>(
        error,
        setError,
        getFieldState
      );
      toast.error("Failed to update overtime entry");
    },
  });

  const onSubmit = (data: unknown) => {
    updateOvertimeMutation.mutate(data as OvertimeEditFormData);
  };

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      // Clear form when dialog is closed
      reset();
      // Reset field array to initial state
      remove();
      append({
        employeeId: "",
        hours: 0,
        rate: 0,
        total: 0,
        notes: "",
      });
    }
  };

  const addEmployee = () => {
    append({
      employeeId: "",
      hours: 0,
      rate: 0,
      total: 0,
      notes: "",
    });
  };

  const removeEmployee = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Overtime Entry</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-6">
            {/* Basic overtime information */}
            <div className="space-y-2">
              <Label required>Date</Label>
              <DatePicker
                date={new Date(watch("date"))}
                onDateChange={(date) =>
                  setValue("date", date?.toISOString() || "")
                }
              />
              {errors.date?.message && (
                <p className="text-sm text-destructive">
                  {errors.date.message as string}
                </p>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Overtime Notes</Label>
              <Textarea
                {...register("notes")}
                placeholder="Enter any additional notes..."
                rows={3}
              />
              {errors.notes?.message && (
                <p className="text-sm text-destructive">
                  {errors.notes.message as string}
                </p>
              )}
            </div>

            <Separator />

            {/* Employees section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Employees</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addEmployee}
                  disabled={!canAddMore}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Employee
                </Button>
              </div>
              {!canAddMore && (
                <p className="text-sm text-muted-foreground">
                  All project members have been added to this overtime entry.
                </p>
              )}
              {fields.map((field, index) => {
                const currentEmployee = employees[index];

                return (
                  <Card className="!bg-transparent" key={field.id}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg -mt-1">
                          Employee {index + 1}
                        </CardTitle>

                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeEmployee(index)}
                            className="text-destructive hover:text-destructive/90"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label required>Employee</Label>
                          <Combobox
                            options={availableEmployeeOptions}
                            value={currentEmployee?.employeeId || ""}
                            onValueChange={(value: string) => {
                              setValue(`employees.${index}.employeeId`, value);
                              handleEmployeeSelect(index, value);
                            }}
                            placeholder="Select employee"
                          />
                          {errors.employees?.[index]?.employeeId?.message && (
                            <p className="text-sm text-destructive">
                              {
                                errors.employees[index].employeeId
                                  .message as string
                              }
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label required>Hours</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            {...register(`employees.${index}.hours`, {
                              valueAsNumber: true,
                              onChange: () => calculateTotal(index),
                            })}
                            placeholder="0"
                            onKeyDown={onKeyDownNumberOnly}
                          />
                          {errors.employees?.[index]?.hours?.message && (
                            <p className="text-sm text-destructive">
                              {errors.employees[index].hours.message as string}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className={`grid gap-4 grid-cols-1 sm:grid-cols-2`}>
                        <div className="space-y-2">
                          <Label required>Rate</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            {...register(`employees.${index}.rate`, {
                              valueAsNumber: true,
                              onChange: () => calculateTotal(index),
                            })}
                            placeholder="0"
                            onKeyDown={onKeyDownNumberOnly}
                            startContent={
                              <span className="text-muted-foreground">
                                {Config.i18n.currency}
                              </span>
                            }
                            endContent={
                              <span className="text-muted-foreground text-xs w-4">
                                /hr
                              </span>
                            }
                          />
                          {errors.employees?.[index]?.rate?.message && (
                            <p className="text-sm text-destructive">
                              {errors.employees[index].rate.message as string}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label>Total</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            {...register(`employees.${index}.total`, {
                              valueAsNumber: true,
                            })}
                            placeholder="0"
                            readOnly
                            disabled
                            className="bg-muted"
                          />
                          {errors.employees?.[index]?.total?.message && (
                            <p className="text-sm text-destructive">
                              {errors.employees[index].total.message as string}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Employee Notes</Label>
                        <Textarea
                          {...register(`employees.${index}.notes`)}
                          placeholder="Enter employee-specific notes"
                          rows={2}
                        />
                        {errors.employees?.[index]?.notes?.message && (
                          <p className="text-sm text-destructive">
                            {errors.employees[index].notes.message as string}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {errors.employees?.message && (
                <p className="text-sm text-destructive">
                  {errors.employees.message as string}
                </p>
              )}

              <div className="flex items-center justify-center">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={addEmployee}
                  disabled={!canAddMore}
                  className="w-full"
                >
                  <Plus className="h-5 w-5" />
                  Add Employee
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateOvertimeMutation.isPending}>
              {updateOvertimeMutation.isPending
                ? "Updating..."
                : "Update Entry"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
